'use client';

import { useRef } from 'react';

import { useControlledState } from '@/hooks/useControlledState';
import { cn } from '@/lib/utils';
import ChevronLeft from '~/svg/IconChevronLeft.svg';
import ChevronRight from '~/svg/IconChevronRight.svg';
import Button from '../Button';
import './styles.css';
import { Tab } from './Tab';
import { useShowScrollButtons } from './tabs.utils';

export type TabsProps<T extends string> = {
  value?: T;
  defaultValue?: T;
  onValueChange?: (value: T) => void;
  tabs: Tab<T>[];
  /** Used in ARIA for accessible tabs */
  label: string;
  classNames?: {
    root?: string;
    tab?: string;
    tabContent?: string;
    innerContentContainer?: string;
    outerContentContainer?: string;
    selectionBar?: string;
    tabsContainer?: string;
    leftButton?: string;
    rightButton?: string;
  };
};

const mutualArrowContainerStyles = cn(
  'absolute right-0 top-0 z-10 flex h-full items-center justify-center ',
  'cursor-pointer',
  'w-8',
  'bg-base-white',
  'active:bg-gray-200',
  'hover:bg-gray-100'
);

export function Tabs<T extends string = string>({
  value: propValue,
  defaultValue,
  onValueChange,
  label,
  tabs,
  classNames,
}: TabsProps<T>) {
  const [value, setValue] = useControlledState({
    prop: propValue,
    defaultProp: defaultValue,
    onChange: onValueChange,
  });

  function handleTabClick(tab: T) {
    setValue(tab);
  }

  const wrapperRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const tabsContainerRef = useRef<HTMLUListElement>(null);

  const {
    showLeftButton,
    showRightButton,
    handleScrollLeft,
    handleScrollRight,
    isScrollable,
  } = useShowScrollButtons({
    tabs,
    tabsRef,
    tabsContainerRef,
  });

  return (
    <div
      ref={wrapperRef}
      className={cn(
        'bg-base-white flex h-full w-full flex-col',
        classNames?.root
      )}
      data-tracker-type='tabs'
    >
      <div
        className={cn(
          'relative border-b-[1px] border-gray-200 dark:border-gray-100',
          classNames?.outerContentContainer
        )}
      >
        {isScrollable && showLeftButton && (
          <Button
            className={cn(
              mutualArrowContainerStyles,
              'left-0',
              classNames?.leftButton
            )}
            onClick={handleScrollLeft}
          >
            {showLeftButton && <ChevronLeft size='24' />}
          </Button>
        )}
        <div>
          <ul
            ref={tabsContainerRef}
            role='tablist'
            aria-label={label}
            className={cn(
              'tabs_hide-scrollbar relative flex w-full overflow-x-scroll',
              classNames?.innerContentContainer
            )}
          >
            <div
              className={cn(
                // NOTE: when modifying the gap here, also update the gap in TAB_GAP_PX in tabs.utils.ts to match
                'flex flex-row items-end gap-8 whitespace-nowrap',
                isScrollable && 'px-8',
                classNames?.tabsContainer
              )}
              ref={tabsRef}
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.value}
                  tab={tab}
                  isActive={value === tab.value}
                  onClick={(tab) => {
                    // prevents an odd over-rendering behaviour when clicking the same tab
                    if (tab.value === value) return;

                    handleTabClick(tab.value);
                  }}
                  classNames={{
                    tab: classNames?.tab,
                    selectionBar: classNames?.selectionBar,
                  }}
                />
              ))}
            </div>
          </ul>
        </div>
        {isScrollable && showRightButton && (
          <Button
            className={cn(
              mutualArrowContainerStyles,
              'right-0',
              classNames?.rightButton
            )}
            onClick={handleScrollRight}
          >
            {showRightButton && <ChevronRight size='24' />}
          </Button>
        )}
      </div>

      <div
        key={`tabpanel-${value}`}
        role='tabpanel'
        id={`tabpanel-${value}`}
        aria-labelledby={`tab-${value}`}
        className={cn(
          'h-full flex-grow overflow-y-auto pt-4',
          classNames?.tabContent
        )}
      >
        {tabs.find((tab) => tab.value === value)?.content}
      </div>
    </div>
  );
}
